import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { ChefHat, FileText, Users, Calendar, Edit3, Sparkles, ChevronLeft, Music2, Truck, Loader2, CheckCircle2, Percent, Printer, Send, Camera, Plane, Cake, MapPin, Flower2, X, ListChecks, Phone, Circle, XCircle, Wallet, Plus, Trash2, Download } from "lucide-react";
import html2pdf from "html2pdf.js";

const fmtEur = (n) => `€ ${Number(n || 0).toLocaleString("it-IT", { minimumFractionDigits: 2 })}`;

const ORDER_STATUS_LABEL = { pending: "Da inviare", sent: "Inviato", delivered: "Consegnato" };
const ORDER_STATUS_CLASS = {
  pending: "bg-amber-100 text-amber-700",
  sent: "bg-blue-100 text-blue-700",
  delivered: "bg-emerald-100 text-emerald-700",
};

const TASK_CAT_LABEL = {
  food_order: "Ordini fornitori",
  shopping: "Lista spesa libera",
  artist: "Artisti",
  photographer: "Fotografo",
  drone: "Dronista",
  cake: "Torta",
  location: "Location",
  decorations: "Addobbi & Fiori",
  drinks: "Bevande",
  staff: "Staff",
  reminder: "Promemoria",
};
const TASK_CAT_COLOR = {
  food_order: "bg-blue-50 text-blue-700 border-blue-200",
  shopping: "bg-amber-50 text-amber-700 border-amber-200",
  artist: "bg-purple-50 text-purple-700 border-purple-200",
  photographer: "bg-indigo-50 text-indigo-700 border-indigo-200",
  drone: "bg-sky-50 text-sky-700 border-sky-200",
  cake: "bg-pink-50 text-pink-700 border-pink-200",
  location: "bg-emerald-50 text-emerald-700 border-emerald-200",
  decorations: "bg-rose-50 text-rose-700 border-rose-200",
  drinks: "bg-cyan-50 text-cyan-700 border-cyan-200",
  staff: "bg-slate-100 text-slate-700 border-slate-200",
  reminder: "bg-yellow-50 text-yellow-800 border-yellow-200",
};

export default function EventDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [ev, setEv] = useState(null);
  const [dishes, setDishes] = useState([]);
  const [artists, setArtists] = useState([]);
  const [loadingMenu, setLoadingMenu] = useState(false);
  const [loadingQuote, setLoadingQuote] = useState(false);
  const [approving, setApproving] = useState(false);
  const [rejectModal, setRejectModal] = useState(false);
  const [rejecting, setRejecting] = useState(false);

  const load = () => api.get(`/events/${id}`).then((r) => { setEv(r.data); });
  useEffect(() => {
    Promise.all([api.get(`/events/${id}`), api.get("/dishes"), api.get("/artists")])
      .then(([e, d, a]) => {
        setEv(e.data); setDishes(d.data); setArtists(a.data);
      })
      .catch(() => toast.error("Evento non trovato"));
  }, [id]);

  const recalc = async () => {
    toast.loading("Ricalcolo costi...", { id: "rc" });
    await api.post(`/events/${id}/calculate`);
    await load();
    toast.success("Costi aggiornati", { id: "rc" });
  };

  const approveQuote = async () => {
    if (!window.confirm("Confermare che il cliente ha ACCETTATO il preventivo? Verranno generati ordini ufficiali ai fornitori e la checklist completa.")) return;
    setApproving(true);
    try {
      await api.post(`/events/${id}/approve`);
      await load();
      toast.success("Preventivo accettato — ordini e checklist generati!");
    } catch (e) { toast.error("Errore approvazione: " + (e?.response?.data?.detail || e.message)); }
    finally { setApproving(false); }
  };

  const rejectQuote = async (reason) => {
    setRejecting(true);
    try {
      await api.put(`/events/${id}/reject`, { reason: reason || "" });
      await load();
      setRejectModal(false);
      toast.success("Preventivo segnato come rifiutato");
    } catch (e) { toast.error("Errore: " + (e?.response?.data?.detail || e.message)); }
    finally { setRejecting(false); }
  };

  const reopenQuote = async () => {
    if (!window.confirm("Riportare il preventivo in stato 'inviato' per poterlo modificare e riproporre al cliente?")) return;
    try {
      await api.put(`/events/${id}/reopen`);
      await load();
      toast.success("Preventivo riaperto");
    } catch (e) { toast.error("Errore: " + (e?.response?.data?.detail || e.message)); }
  };

  const updateOrderStatus = async (orderNumber, status) => {
    await api.put(`/events/${id}/orders/${orderNumber}/status`, { status });
    await load();
    toast.success(`Ordine ${orderNumber}: ${ORDER_STATUS_LABEL[status]}`);
  };

  const toggleTask = async (taskId, currentStatus) => {
    const next = currentStatus === "done" ? "pending" : "done";
    await api.put(`/events/${id}/tasks/${taskId}`, { status: next });
    await load();
  };

  const printChecklist = () => {
    if (!ev?.tasks?.length) return;
    const w = window.open("", "_blank");
    if (!w) return;
    const groups = ev.tasks.reduce((acc, t) => {
      const cat = TASK_CAT_LABEL[t.category] || t.category;
      (acc[cat] = acc[cat] || []).push(t);
      return acc;
    }, {});
    const rowsHtml = Object.entries(groups).map(([cat, ts]) => {
      const items = ts.map((t) => `<tr><td style="padding:8px 6px;border-bottom:1px solid #eee">☐</td><td style="padding:8px 6px;border-bottom:1px solid #eee"><strong>${t.title}</strong>${t.description ? `<br><span style="color:#64748B;font-size:12px">${t.description.replace(/</g, "&lt;")}</span>` : ""}${t.contact ? `<br><span style="color:#64748B;font-size:12px">📞 ${t.contact}</span>` : ""}</td><td style="padding:8px 6px;border-bottom:1px solid #eee;text-align:right;color:#2563EB">${t.due_date || "—"}</td></tr>`).join("");
      return `<h2 style="color:#0F2741;margin-top:24px;border-bottom:2px solid #EFF6FF;padding-bottom:4px">${cat}</h2><table style="width:100%;border-collapse:collapse"><tbody>${items}</tbody></table>`;
    }).join("");
    w.document.write(`<!doctype html><html><head><title>Checklist ${ev.client_name || ev.id}</title><style>body{font-family:Georgia,serif;max-width:820px;margin:32px auto;padding:0 24px;color:#0F2741}h1{color:#2563EB}</style></head><body>
      <h1>Checklist — ${ev.client_name || "Evento"} · ${ev.event_date}</h1>
      <p>${ev.guests} ospiti · approvato il ${ev.approved_at ? new Date(ev.approved_at).toLocaleDateString("it-IT") : "—"}</p>
      ${rowsHtml}
      <p style="margin-top:40px;font-size:11px;color:#64748B">Event System 360 · ${new Date().toLocaleString("it-IT")}</p>
      <script>window.print()</script></body></html>`);
    w.document.close();
  };

  const printOrder = (o) => {
    const w = window.open("", "_blank");
    if (!w) return;
    const itemsRows = (o.items || []).map((it) => `<tr><td style="padding:6px;border-bottom:1px solid #eee;text-transform:capitalize">${it.ingredient}</td><td style="padding:6px;text-align:right;border-bottom:1px solid #eee">${it.quantity_kg.toFixed(2)} kg</td><td style="padding:6px;text-align:right;border-bottom:1px solid #eee">€${it.price_per_kg.toFixed(2)}</td><td style="padding:6px;text-align:right;border-bottom:1px solid #eee">€${it.total.toFixed(2)}</td></tr>`).join("");
    w.document.write(`<!doctype html><html><head><title>${o.order_number}</title><style>body{font-family:Georgia,serif;max-width:800px;margin:40px auto;padding:0 20px;color:#0F2741}h1{color:#2563EB;border-bottom:2px solid #2563EB;padding-bottom:8px}table{width:100%;border-collapse:collapse;margin-top:20px}th{padding:6px;text-align:left;background:#EFF6FF;color:#0F2741;border-bottom:2px solid #2563EB}th:nth-child(n+2){text-align:right}.tot{font-size:24px;font-weight:bold;color:#2563EB}</style></head><body>
      <h1>Ordine ${o.order_number}</h1>
      <p><strong>Fornitore:</strong> ${o.supplier_name}<br/><strong>Data ordine:</strong> ${new Date(o.created_at).toLocaleDateString("it-IT")}<br/><strong>Per evento del:</strong> ${o.event_date} · ${o.guests} ospiti (${o.portions} porzioni con +5%)</p>
      <table><thead><tr><th>Ingrediente</th><th>Quantità</th><th>€/kg</th><th>Totale</th></tr></thead><tbody>${itemsRows}</tbody></table>
      <p style="text-align:right;margin-top:30px"><span class="tot">Totale: €${o.total.toFixed(2)}</span></p>
      <p style="margin-top:60px;font-size:12px;color:#64748B">Generato da Event System 360 · ${new Date().toLocaleString("it-IT")}</p>
      <script>window.print()</script></body></html>`);
    w.document.close();
  };

  const printQuote = () => {
    if (!ev) return;
    const w = window.open("", "_blank");
    if (!w) return;
    const breakdown = ev.cost_breakdown || {};
    const drinks = (ev.drinks_breakdown || []).map((b) => `<tr><td style="padding:6px">${b.label}</td><td style="text-align:right;padding:6px">${b.bottles} unità</td><td style="text-align:right;padding:6px">€${Number(b.total).toFixed(2)}</td></tr>`).join("");
    const eventTypeLabel = (ev.event_type || "").replace(/_/g, " ");
    w.document.write(`<!doctype html><html><head><title>Preventivo ${ev.client_name || ev.id}</title><style>
      @page{margin:30px}
      body{font-family:Georgia,'Playfair Display',serif;max-width:760px;margin:30px auto;padding:0 30px;color:#0F2741;line-height:1.6}
      .top{display:flex;justify-content:space-between;align-items:flex-end;border-bottom:3px double #2563EB;padding-bottom:14px;margin-bottom:30px}
      .brand{color:#2563EB;font-weight:700;font-size:14px;letter-spacing:3px;text-transform:uppercase}
      h1{font-size:36px;margin:6px 0 2px 0;font-weight:600}
      .subtitle{color:#64748B;font-size:14px}
      h2{color:#2563EB;font-size:18px;text-transform:uppercase;letter-spacing:2px;margin-top:30px;border-bottom:1px solid #E2E8F0;padding-bottom:6px}
      .quote-text{white-space:pre-line;font-family:Georgia,serif;font-size:15px;color:#334155}
      table{width:100%;border-collapse:collapse;margin-top:10px;font-family:'Helvetica',sans-serif;font-size:13px}
      td{padding:6px;border-bottom:1px solid #F1F5F9}
      .total-box{margin-top:30px;background:linear-gradient(135deg,#2563EB,#0F2741);color:white;padding:24px;border-radius:12px;display:flex;justify-content:space-between;align-items:center}
      .total-box .lbl{font-size:12px;text-transform:uppercase;letter-spacing:2px;opacity:0.8}
      .total-box .amt{font-size:36px;font-weight:700}
      .foot{margin-top:60px;font-size:11px;color:#94A3B8;text-align:center}
    </style></head><body>
      <div class="top">
        <div>
          <p class="brand">Event System 360</p>
          <h1>Preventivo Evento</h1>
          <p class="subtitle">Numero: ${ev.id.substring(0,8).toUpperCase()} · Emesso il ${new Date().toLocaleDateString("it-IT")}</p>
        </div>
        <div style="text-align:right">
          <p class="subtitle">Gentile cliente</p>
          <h2 style="margin:0;color:#0F2741;font-size:22px;border:none;letter-spacing:0;text-transform:none">${ev.client_name || "—"}</h2>
        </div>
      </div>

      <div style="background:#EFF6FF;border-left:4px solid #2563EB;padding:14px 20px;border-radius:6px;margin-bottom:24px">
        <p style="margin:0;font-size:13px;color:#64748B"><strong style="color:#0F2741">Tipologia:</strong> ${eventTypeLabel} &nbsp;·&nbsp; <strong style="color:#0F2741">Data:</strong> ${ev.event_date} &nbsp;·&nbsp; <strong style="color:#0F2741">Ospiti:</strong> ${ev.guests} &nbsp;·&nbsp; <strong style="color:#0F2741">Orario:</strong> ${ev.start_time}–${ev.end_time}</p>
      </div>

      <h2>Presentazione</h2>
      <div class="quote-text">${(ev.quote_text || "Preventivo non ancora generato dall'AI.").replace(/</g, "&lt;")}</div>

      ${ev.menu_text ? `<h2>Menù dell'evento</h2><div style="background:#FFFBEB;border:1px solid #FDE68A;padding:20px;border-radius:8px;white-space:pre-line;font-family:Georgia,serif;font-style:italic">${ev.menu_text.replace(/</g, "&lt;")}</div>` : ""}

      <h2>Dettaglio costi</h2>
      <table>
        <tr><td>🍽️ Servizio cucina e materie prime</td><td style="text-align:right">€ ${Number(breakdown.food || 0).toFixed(2)}</td></tr>
        ${drinks ? `<tr><td colspan="2"><strong>Bevande incluse:</strong></td></tr>${drinks}<tr><td><em>Totale bevande</em></td><td style="text-align:right">€ ${Number(breakdown.drinks || 0).toFixed(2)}</td></tr>` : `<tr><td>🥂 Bevande</td><td style="text-align:right">€ ${Number(breakdown.drinks || 0).toFixed(2)}</td></tr>`}
        <tr><td>🎭 Intrattenimento e artisti</td><td style="text-align:right">€ ${Number(breakdown.artists || 0).toFixed(2)}</td></tr>
        <tr><td>👨‍🍳 Personale di sala e cucina</td><td style="text-align:right">€ ${Number(breakdown.staff || 0).toFixed(2)}</td></tr>
      </table>

      <div class="total-box">
        <div><div class="lbl">Prezzo finale</div><div style="font-size:11px;opacity:0.8">${ev.payment_terms?.vat_included === false ? "IVA esclusa" : "IVA inclusa"}${Number(ev.markup_pct_applied || 0) > 0 ? ` · ricarico ${ev.markup_pct_applied}%` : ""}</div></div>
        <div class="amt">€ ${Number(ev.final_price || 0).toLocaleString("it-IT", {minimumFractionDigits: 2})}</div>
      </div>

      ${(ev.payment_terms?.schedule || []).length > 0 ? `
        <h2>Modalità di pagamento</h2>
        <table style="font-size:14px">
          <thead><tr style="background:#EFF6FF"><th style="text-align:left;padding:8px;color:#0F2741">Rata</th><th style="text-align:center;padding:8px;color:#0F2741">Scadenza</th><th style="text-align:right;padding:8px;color:#0F2741">%</th><th style="text-align:right;padding:8px;color:#0F2741">Importo</th></tr></thead>
          <tbody>
            ${ev.payment_terms.schedule.map((s) => {
              const amount = (Number(ev.final_price || 0) * (s.pct / 100));
              let when = "—";
              if (s.due_on_acceptance) when = "All'accettazione";
              else if (s.days_before === 0) when = "Giorno dell'evento";
              else if (s.days_before > 0) when = `${s.days_before} giorni prima dell'evento`;
              return `<tr><td style="padding:8px">${s.label}</td><td style="text-align:center;padding:8px;color:#64748B">${when}</td><td style="text-align:right;padding:8px;font-weight:600;color:#2563EB">${s.pct}%</td><td style="text-align:right;padding:8px;font-weight:600">€ ${amount.toLocaleString("it-IT", {minimumFractionDigits: 2})}</td></tr>`;
            }).join("")}
          </tbody>
        </table>
        ${ev.payment_terms.notes ? `<p style="font-size:12px;color:#64748B;margin-top:8px;font-style:italic">${ev.payment_terms.notes.replace(/</g, "&lt;")}</p>` : ""}
      ` : ""}

      <h2 style="page-break-before:auto">Termini e condizioni</h2>
      <ol style="font-size:12px;color:#475569;line-height:1.6;padding-left:20px">
        ${((ev.payment_terms?.legal_clauses) || []).map((c) => `<li style="margin-bottom:6px">${c.replace(/</g, "&lt;")}</li>`).join("")}
      </ol>

      <h2 style="margin-top:40px">Firma per accettazione</h2>
      <p style="font-size:13px;color:#334155;margin-bottom:30px">Il sottoscritto cliente dichiara di aver letto, compreso e accettato integralmente il presente preventivo, comprese le modalità di pagamento e le condizioni generali sopra riportate.</p>
      <table style="width:100%;margin-top:50px;border:none">
        <tr style="border:none">
          <td style="border:none;width:50%;padding:0 16px 0 0">
            <div style="border-bottom:1.5px solid #0F2741;height:50px"></div>
            <p style="margin:4px 0 0;font-size:11px;color:#64748B;text-transform:uppercase;letter-spacing:1.5px">Firma del cliente</p>
            <p style="margin:0;font-size:13px;color:#0F2741;font-weight:600">${ev.client_name || "_________________"}</p>
          </td>
          <td style="border:none;width:25%;padding:0 8px">
            <div style="border-bottom:1.5px solid #0F2741;height:50px"></div>
            <p style="margin:4px 0 0;font-size:11px;color:#64748B;text-transform:uppercase;letter-spacing:1.5px">Data</p>
          </td>
          <td style="border:none;width:25%;padding:0 0 0 8px">
            <div style="border-bottom:1.5px solid #0F2741;height:50px"></div>
            <p style="margin:4px 0 0;font-size:11px;color:#64748B;text-transform:uppercase;letter-spacing:1.5px">Luogo</p>
          </td>
        </tr>
      </table>

      <p style="margin-top:40px;font-size:11px;color:#94A3B8;border-top:1px solid #E2E8F0;padding-top:12px">
        <strong>Approvazione specifica delle clausole vessatorie (artt. 1341 e 1342 c.c.)</strong>: il cliente dichiara di approvare specificamente le clausole relative a disdetta/penali, foro competente, limitazioni di responsabilità e variazioni del numero ospiti.
      </p>
      <div style="margin-top:8px">
        <div style="border-bottom:1.5px solid #0F2741;width:60%;height:35px"></div>
        <p style="margin:4px 0 0;font-size:11px;color:#64748B">Firma per approvazione specifica clausole</p>
      </div>

      <p class="foot">Event System 360 by renova 360 · Documento generato il ${new Date().toLocaleString("it-IT")}</p>
      <script>setTimeout(()=>window.print(),300)</script></body></html>`);
    w.document.close();
  };

  const downloadQuotePDF = async () => {
    if (!ev) return;
    toast.loading("Generazione PDF in corso...", { id: "pdf" });
    try {
      const breakdown = ev.cost_breakdown || {};
      const drinks = (ev.drinks_breakdown || []).map((b) => `<tr><td style="padding:6px">${b.label}</td><td style="text-align:right;padding:6px">${b.bottles} unità</td><td style="text-align:right;padding:6px">€${Number(b.total).toFixed(2)}</td></tr>`).join("");
      const eventTypeLabel = (ev.event_type || "").replace(/_/g, " ");
      const container = document.createElement("div");
      container.style.cssText = "position:fixed;left:-99999px;top:0;width:760px;background:#fff;";
      container.innerHTML = `<div style="font-family:Georgia,'Playfair Display',serif;padding:30px;color:#0F2741;line-height:1.6">
        <div style="display:flex;justify-content:space-between;align-items:flex-end;border-bottom:3px double #2563EB;padding-bottom:14px;margin-bottom:30px">
          <div>
            <div style="color:#2563EB;font-weight:700;font-size:14px;letter-spacing:3px;text-transform:uppercase">Preventivo</div>
            <h1 style="font-size:36px;margin:6px 0 2px 0;font-weight:600">${ev.client_name || "Gentile Cliente"}</h1>
            <div style="color:#64748B;font-size:14px">${ev.event_date || ""} · ${ev.guests || 0} ospiti · ${eventTypeLabel}</div>
          </div>
          <div style="text-align:right;font-size:11px;color:#64748B">Event System 360<br>${new Date().toLocaleDateString("it-IT")}</div>
        </div>
        <h2 style="color:#2563EB;font-size:18px;text-transform:uppercase;letter-spacing:2px;margin-top:30px;border-bottom:1px solid #E2E8F0;padding-bottom:6px">Riassunto evento</h2>
        <div style="font-size:14px;white-space:pre-line;color:#334155;line-height:1.8">${(ev.quote_text || "").replace(/</g, "&lt;")}</div>
        ${drinks ? `<h2 style="color:#2563EB;font-size:18px;text-transform:uppercase;letter-spacing:2px;margin-top:30px;border-bottom:1px solid #E2E8F0;padding-bottom:6px">Bevande</h2><table style="width:100%;font-size:14px;border-collapse:collapse"><tbody>${drinks}</tbody></table>` : ""}
        <div style="background:#0F2741;color:#fff;padding:22px 26px;border-radius:10px;margin:30px 0;display:flex;justify-content:space-between;align-items:center">
          <div><div style="color:#93C5FD;font-size:11px;letter-spacing:2px;text-transform:uppercase">Prezzo finale</div><div style="font-size:11px;opacity:0.8">${ev.payment_terms?.vat_included === false ? "IVA esclusa" : "IVA inclusa"}${Number(ev.markup_pct_applied || 0) > 0 ? ` · ricarico ${ev.markup_pct_applied}%` : ""}</div></div>
          <div style="font-size:32px;font-weight:600">€ ${Number(ev.final_price || 0).toLocaleString("it-IT", {minimumFractionDigits: 2})}</div>
        </div>
        ${(ev.payment_terms?.schedule || []).length > 0 ? `
          <h2 style="color:#2563EB;font-size:18px;text-transform:uppercase;letter-spacing:2px;margin-top:30px;border-bottom:1px solid #E2E8F0;padding-bottom:6px">Modalità di pagamento</h2>
          <table style="width:100%;font-size:14px;border-collapse:collapse">
            <thead><tr style="background:#EFF6FF"><th style="text-align:left;padding:8px;color:#0F2741">Rata</th><th style="text-align:center;padding:8px;color:#0F2741">Scadenza</th><th style="text-align:right;padding:8px;color:#0F2741">%</th><th style="text-align:right;padding:8px;color:#0F2741">Importo</th></tr></thead>
            <tbody>
              ${ev.payment_terms.schedule.map((s) => {
                const amount = (Number(ev.final_price || 0) * (s.pct / 100));
                let when = "—";
                if (s.due_on_acceptance) when = "All'accettazione";
                else if (s.days_before === 0) when = "Giorno dell'evento";
                else if (s.days_before > 0) when = `${s.days_before} giorni prima dell'evento`;
                return `<tr><td style="padding:8px">${s.label}</td><td style="text-align:center;padding:8px;color:#64748B">${when}</td><td style="text-align:right;padding:8px;font-weight:600;color:#2563EB">${s.pct}%</td><td style="text-align:right;padding:8px;font-weight:600">€ ${amount.toLocaleString("it-IT", {minimumFractionDigits: 2})}</td></tr>`;
              }).join("")}
            </tbody>
          </table>
          ${ev.payment_terms.notes ? `<p style="font-size:12px;color:#64748B;margin-top:8px;font-style:italic">${ev.payment_terms.notes.replace(/</g, "&lt;")}</p>` : ""}
        ` : ""}
        <h2 style="color:#2563EB;font-size:18px;text-transform:uppercase;letter-spacing:2px;margin-top:30px;border-bottom:1px solid #E2E8F0;padding-bottom:6px">Termini e condizioni</h2>
        <ol style="font-size:12px;color:#475569;line-height:1.6;padding-left:20px">
          ${((ev.payment_terms?.legal_clauses) || []).map((c) => `<li style="margin-bottom:6px">${c.replace(/</g, "&lt;")}</li>`).join("")}
        </ol>
        <h2 style="color:#2563EB;font-size:18px;text-transform:uppercase;letter-spacing:2px;margin-top:40px;border-bottom:1px solid #E2E8F0;padding-bottom:6px">Firma per accettazione</h2>
        <p style="font-size:13px;color:#334155;margin-bottom:30px">Il sottoscritto cliente dichiara di aver letto, compreso e accettato integralmente il presente preventivo, comprese le modalità di pagamento e le condizioni generali sopra riportate.</p>
        <table style="width:100%;margin-top:30px"><tr>
          <td style="width:50%;padding:0 16px 0 0"><div style="border-bottom:1.5px solid #0F2741;height:50px"></div><p style="margin:4px 0 0;font-size:11px;color:#64748B;text-transform:uppercase;letter-spacing:1.5px">Firma del cliente</p><p style="margin:0;font-size:13px;color:#0F2741;font-weight:600">${ev.client_name || "_________________"}</p></td>
          <td style="width:25%;padding:0 8px"><div style="border-bottom:1.5px solid #0F2741;height:50px"></div><p style="margin:4px 0 0;font-size:11px;color:#64748B;text-transform:uppercase;letter-spacing:1.5px">Data</p></td>
          <td style="width:25%;padding:0 0 0 8px"><div style="border-bottom:1.5px solid #0F2741;height:50px"></div><p style="margin:4px 0 0;font-size:11px;color:#64748B;text-transform:uppercase;letter-spacing:1.5px">Luogo</p></td>
        </tr></table>
        <p style="margin-top:30px;font-size:11px;color:#94A3B8;border-top:1px solid #E2E8F0;padding-top:12px"><strong>Approvazione specifica delle clausole vessatorie (artt. 1341 e 1342 c.c.)</strong>: il cliente dichiara di approvare specificamente le clausole relative a disdetta/penali, foro competente, limitazioni di responsabilità e variazioni del numero ospiti.</p>
        <div style="margin-top:8px"><div style="border-bottom:1.5px solid #0F2741;width:60%;height:35px"></div><p style="margin:4px 0 0;font-size:11px;color:#64748B">Firma per approvazione specifica clausole</p></div>
        <p style="margin-top:30px;color:#94A3B8;font-size:11px;text-align:center">Event System 360 by renova 360 · Documento generato il ${new Date().toLocaleString("it-IT")}</p>
      </div>`;
      document.body.appendChild(container);
      const filename = `Preventivo_${(ev.client_name || "Cliente").replace(/[^a-zA-Z0-9]/g, "_")}_${ev.event_date || ""}.pdf`;
      await html2pdf().from(container).set({
        margin: 10,
        filename,
        image: { type: "jpeg", quality: 0.95 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
        pagebreak: { mode: ["avoid-all", "css", "legacy"] },
      }).save();
      document.body.removeChild(container);
      toast.success("PDF scaricato!", { id: "pdf" });
    } catch (e) {
      console.error(e);
      toast.error("Errore generazione PDF", { id: "pdf" });
    }
  };

  const genMenu = async () => {
    setLoadingMenu(true);
    try {
      await api.post(`/events/${id}/generate-menu`);
      await load();
      toast.success("Menù generato con AI");
    } catch (e) { toast.error("Errore AI menù"); } finally { setLoadingMenu(false); }
  };

  const genQuote = async () => {
    setLoadingQuote(true);
    try {
      if (!ev.total_cost) {
        await api.post(`/events/${id}/calculate`);
      }
      await api.post(`/events/${id}/generate-quote`);
      await load();
      toast.success("Preventivo generato!");
    } catch (e) { toast.error("Errore AI preventivo"); } finally { setLoadingQuote(false); }
  };

  if (!ev) return <div className="p-12 text-center text-slate-400">Caricamento...</div>;

  const chosenDishes = dishes.filter((d) => Object.values(ev.dishes || {}).flat().includes(d.id));
  const legacyArtistIds = (ev.timeline || []).filter((t) => !t.skip && t.artist_id).map((t) => t.artist_id);
  const bookingIds = (ev.artists_bookings || []).map((b) => b.artist_id).filter(Boolean);
  const chosenArtists = artists.filter((a) => [...legacyArtistIds, ...bookingIds].includes(a.id));
  const photographerIds = (ev.photographers_bookings && ev.photographers_bookings.length)
    ? ev.photographers_bookings.map((b) => b.artist_id).filter(Boolean)
    : (ev.photographer_id ? [ev.photographer_id] : []);
  const droneIds = (ev.drones_bookings && ev.drones_bookings.length)
    ? ev.drones_bookings.map((b) => b.artist_id).filter(Boolean)
    : (ev.drone_id ? [ev.drone_id] : []);
  const photographers = artists.filter((a) => photographerIds.includes(a.id));
  const drones = artists.filter((a) => droneIds.includes(a.id));
  const breakdown = ev.cost_breakdown || {};
  const priceBd = ev.price_breakdown || {};
  const budgetTarget = Number(ev.magic_budget_target || 0);
  const isOverBudget = budgetTarget > 0 && Number(ev.final_price || 0) > budgetTarget * 1.05;
  const overBudgetPct = budgetTarget > 0 ? Math.round(((Number(ev.final_price || 0) - budgetTarget) / budgetTarget) * 100) : 0;

  return (
    <div className="space-y-8" data-testid="event-detail">
      <button onClick={() => navigate("/eventi")} className="btn-ghost"><ChevronLeft className="w-4 h-4" /> Tutti gli eventi</button>
      <div className="flex flex-col md:flex-row justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <p className="text-xs uppercase tracking-[0.18em] text-[#2563EB] font-semibold font-ui">{ev.event_type}</p>
            <span className={`text-xs px-2 py-0.5 rounded-full font-semibold uppercase tracking-wider ${
              ev.status === "approved" ? "bg-emerald-100 text-emerald-700" :
              ev.status === "rejected" ? "bg-rose-100 text-rose-700" :
              ev.status === "quoted" ? "bg-blue-100 text-blue-700" :
              "bg-amber-100 text-amber-700"
            }`} data-testid="event-status">{
              ev.status === "approved" ? "Accettato" :
              ev.status === "rejected" ? "Rifiutato" :
              ev.status === "quoted" ? "Inviato" :
              ev.status
            }</span>
          </div>
          <h1 className="font-display text-4xl md:text-5xl text-[#0F2741] tracking-tight mt-2">{ev.client_name || "Evento"}</h1>
          <p className="text-slate-500 mt-2 flex items-center gap-4 flex-wrap">
            <span className="flex items-center gap-1.5"><Calendar className="w-4 h-4" />{ev.event_date} · {ev.start_time}-{ev.end_time}</span>
            <span className="flex items-center gap-1.5"><Users className="w-4 h-4" />{ev.guests} ospiti ({ev.portions || Math.ceil(ev.guests * 1.05)} porzioni)</span>
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => navigate(`/eventi/${id}/modifica`)} className="btn-ghost border border-slate-200"><Edit3 className="w-4 h-4" /> Modifica</button>
          <button onClick={recalc} className="btn-ghost border border-slate-200">Ricalcola</button>
          {ev.status !== "approved" && ev.status !== "rejected" && (
            <button onClick={genQuote} disabled={loadingQuote} data-testid="btn-generate-quote" className="btn-primary">
              {loadingQuote ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Genera preventivo AI
            </button>
          )}
          {ev.status === "quoted" && (
            <>
              <button onClick={() => setRejectModal(true)} data-testid="btn-reject-quote" className="btn-ghost border border-rose-200 text-rose-600 hover:bg-rose-50">
                <XCircle className="w-4 h-4" /> Preventivo Rifiutato
              </button>
              <button onClick={approveQuote} disabled={approving} data-testid="btn-approve-quote" className="btn-primary bg-emerald-600 hover:bg-emerald-700">
                {approving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Preventivo Accettato
              </button>
            </>
          )}
          {ev.status === "rejected" && (
            <button onClick={reopenQuote} data-testid="btn-reopen-quote" className="btn-ghost border border-slate-200">
              <Sparkles className="w-4 h-4" /> Riapri preventivo
            </button>
          )}
        </div>
      </div>

      {/* Rejected banner */}
      {ev.status === "rejected" && (
        <div className="bg-rose-50 border border-rose-200 rounded-xl p-5 flex items-start gap-3" data-testid="rejected-banner">
          <XCircle className="w-6 h-6 text-rose-500 shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-ui font-semibold text-rose-700">Preventivo rifiutato dal cliente</p>
            <p className="text-sm text-rose-600 mt-1">
              Rifiutato il {ev.rejected_at ? new Date(ev.rejected_at).toLocaleString("it-IT") : "—"}
            </p>
            {ev.rejection_reason && (
              <p className="text-sm text-slate-700 mt-2 italic">"{ev.rejection_reason}"</p>
            )}
          </div>
        </div>
      )}

      {/* Markup per categoria */}
      <MarkupByCategoryEditor ev={ev} eventId={id} onUpdate={recalc} />

      {/* Budget alert banner */}
      {isOverBudget && (
        <div className="bg-rose-50 border-2 border-rose-300 rounded-xl p-4 flex items-start gap-3" data-testid="budget-over-banner">
          <span className="text-2xl">⚠️</span>
          <div className="flex-1">
            <p className="font-semibold text-rose-700">Budget cliente sforato di {overBudgetPct}%</p>
            <p className="text-sm text-rose-600 mt-0.5">
              Budget cliente: <strong>€{budgetTarget.toFixed(0)}</strong> · Prezzo finale: <strong>€{Number(ev.final_price || 0).toFixed(0)}</strong>.
              Le voci in <span className="text-rose-700 font-semibold">rosso</span> qui sotto sono quelle che pesano di più — valuta se ridurle o discutere col cliente.
            </p>
          </div>
        </div>
      )}

      {/* Cost summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
        <CostCard label="Cibo (food cost)" value={fmtEur(breakdown.food)} sale={fmtEur(priceBd.food ?? breakdown.food)} red={isOverBudget && Number(priceBd.food || 0) > budgetTarget * 0.4} />
        <CostCard label="Bevande" value={fmtEur(breakdown.drinks)} sale={fmtEur(priceBd.drinks ?? breakdown.drinks)} red={isOverBudget && Number(priceBd.drinks || 0) > budgetTarget * 0.25} />
        <CostCard label="Staff" value={fmtEur(breakdown.staff)} sale={fmtEur(priceBd.staff ?? breakdown.staff)} red={isOverBudget && Number(priceBd.staff || 0) > budgetTarget * 0.25} />
        <CostCard label="Artisti" value={fmtEur(breakdown.artists)} sale={fmtEur(priceBd.artists ?? breakdown.artists)} red={isOverBudget && Number(priceBd.artists || 0) > budgetTarget * 0.20} />
        <CostCard label="Fotografi/Drone" value={fmtEur(breakdown.photographers || 0)} sale={fmtEur(priceBd.photographers ?? breakdown.photographers ?? 0)} red={isOverBudget && Number(priceBd.photographers || 0) > budgetTarget * 0.20} />
        <CostCard label="Torta" value={fmtEur(breakdown.cake)} sale={fmtEur(priceBd.cake ?? breakdown.cake)} red={isOverBudget && Number(priceBd.cake || 0) > budgetTarget * 0.15} />
        <CostCard label="Location" value={fmtEur(breakdown.location)} sale={fmtEur(priceBd.location ?? breakdown.location)} red={isOverBudget && Number(priceBd.location || 0) > budgetTarget * 0.20} />
        <CostCard label="Addobbi" value={fmtEur(breakdown.decorations)} sale={fmtEur(priceBd.decorations ?? breakdown.decorations)} red={isOverBudget && Number(priceBd.decorations || 0) > budgetTarget * 0.15} />
      </div>
      <div className="p-5 rounded-xl bg-gradient-to-br from-[#2563EB] to-[#0F2741] text-white flex justify-between items-center">
        <div>
          <p className="text-xs uppercase tracking-wider font-semibold opacity-80">Costo totale (interno)</p>
          <p className="font-display text-2xl mt-1">{fmtEur(ev.total_cost)}</p>
        </div>
        <div className="text-right">
          <p className="text-xs uppercase tracking-wider font-semibold opacity-80">Prezzo finale al cliente</p>
          <p className="font-display text-3xl mt-1">{fmtEur(ev.final_price)}</p>
          {Number(ev.markup_pct_applied || 0) > 0 && (
            <p className="text-xs opacity-80">Ricarico medio: {ev.markup_pct_applied}%</p>
          )}
        </div>
      </div>

      {/* Payment Terms */}
      <PaymentTermsSection ev={ev} eventId={id} onUpdate={load} />

      {/* Location */}
      {ev.location?.name && (
        <Section title="Location" icon={MapPin}>
          <div className="flex justify-between items-start gap-4">
            <div>
              <p className="font-display text-xl text-[#0F2741]">{ev.location.name}</p>
              {ev.location.address && <p className="text-sm text-slate-500 mt-1">{ev.location.address}</p>}
              {ev.location.capacity > 0 && <p className="text-xs text-slate-400 mt-1">Capienza max: {ev.location.capacity} persone</p>}
              <span className={`inline-block mt-2 text-xs px-2 py-0.5 rounded-full ${ev.location.external ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>
                {ev.location.external ? "Esterna" : "Interna"}
              </span>
            </div>
            {ev.location.external && ev.location.price > 0 && (
              <p className="font-display text-2xl text-[#2563EB] shrink-0">{fmtEur(ev.location.price)}</p>
            )}
          </div>
        </Section>
      )}

      {/* Decorations */}
      {ev.decorations?.enabled && (
        <Section title="Addobbi & Fiori" icon={Flower2}>
          {ev.decorations.theme && <p className="text-sm text-slate-600 mb-3"><strong>Tema:</strong> {ev.decorations.theme}</p>}
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500"><tr><th className="text-left px-3 py-2">Voce</th><th className="text-right px-3 py-2">Qtà</th><th className="text-right px-3 py-2">€ unità</th><th className="text-right px-3 py-2">Totale</th></tr></thead>
            <tbody>
              {(ev.decorations.items || []).map((it, i) => (
                <tr key={i} className="border-t border-slate-100">
                  <td className="px-3 py-2">{it.icon || "✨"} {it.name}</td>
                  <td className="px-3 py-2 text-right">{it.qty || 1}</td>
                  <td className="px-3 py-2 text-right">€{Number(it.price || 0).toFixed(2)}</td>
                  <td className="px-3 py-2 text-right font-semibold">{fmtEur((it.qty || 1) * (it.price || 0))}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {ev.decorations.notes && <p className="text-xs text-slate-500 mt-3 italic">Note: {ev.decorations.notes}</p>}
        </Section>
      )}

      {/* Staff */}
      {ev.staff_plan && (
        <Section title="Pianificazione Staff" icon={Users}>
          <StaffEditor ev={ev} eventId={id} onUpdate={recalc} />
        </Section>
      )}

      {/* Drinks breakdown */}
      {ev.drinks_breakdown && ev.drinks_breakdown.length > 0 && (
        <Section title="Bevande incluse" icon={ChefHat}>
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr><th className="text-left px-3 py-2">Tipologia</th><th className="text-right px-3 py-2">Unità stimate</th><th className="text-right px-3 py-2">€/unità</th><th className="text-right px-3 py-2">Totale</th></tr>
            </thead>
            <tbody>
              {ev.drinks_breakdown.map((b, i) => (
                <tr key={i} className="border-t border-slate-100">
                  <td className="px-3 py-2">{b.label}</td>
                  <td className="px-3 py-2 text-right">{b.bottles}</td>
                  <td className="px-3 py-2 text-right">€{Number(b.unit_price).toFixed(2)}</td>
                  <td className="px-3 py-2 text-right font-semibold">{fmtEur(b.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>
      )}

      {/* Checklist post-approvazione */}
      {ev.status === "approved" && (ev.tasks || []).length > 0 && (
        <Section title="Checklist post-approvazione" icon={ListChecks} action={
          <div className="flex items-center gap-3">
            <span className="text-xs text-slate-500 font-ui" data-testid="checklist-progress">
              {(ev.tasks || []).filter((t) => t.status === "done").length}/{(ev.tasks || []).length} completati
            </span>
            <button onClick={printChecklist} data-testid="btn-print-checklist" className="btn-ghost border border-slate-200">
              <Printer className="w-3.5 h-3.5" /> Stampa
            </button>
          </div>
        }>
          <div className="space-y-5" data-testid="event-checklist">
            {Object.entries((ev.tasks || []).reduce((acc, t) => {
              (acc[t.category] = acc[t.category] || []).push(t);
              return acc;
            }, {})).map(([cat, tasks]) => (
              <div key={cat}>
                <div className="flex items-center gap-2 mb-2">
                  <span className={`text-xs px-2 py-0.5 rounded-full border font-semibold uppercase tracking-wider ${TASK_CAT_COLOR[cat] || "bg-slate-100 text-slate-700 border-slate-200"}`}>
                    {TASK_CAT_LABEL[cat] || cat}
                  </span>
                  <span className="text-xs text-slate-400">· {tasks.filter((t) => t.status === "done").length}/{tasks.length}</span>
                </div>
                <div className="space-y-2">
                  {tasks.map((t) => {
                    const done = t.status === "done";
                    return (
                      <button
                        key={t.id}
                        onClick={() => toggleTask(t.id, t.status)}
                        data-testid={`task-${t.id}`}
                        className={`w-full text-left flex items-start gap-3 p-3 rounded-lg border transition-colors ${done ? "bg-emerald-50/50 border-emerald-200" : "bg-white border-slate-200 hover:border-[#2563EB]"}`}
                      >
                        {done ? (
                          <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" />
                        ) : (
                          <Circle className="w-5 h-5 text-slate-300 shrink-0 mt-0.5" />
                        )}
                        <div className="flex-1 min-w-0">
                          <p className={`font-ui font-semibold text-sm ${done ? "text-slate-400 line-through" : "text-[#0F2741]"}`}>{t.title}</p>
                          {t.description && (
                            <p className={`text-xs mt-1 ${done ? "text-slate-400" : "text-slate-500"}`}>{t.description}</p>
                          )}
                          {t.contact && (
                            <p className={`text-xs mt-1 flex items-center gap-1 ${done ? "text-slate-400" : "text-[#2563EB]"}`}>
                              <Phone className="w-3 h-3" />{t.contact}
                            </p>
                          )}
                        </div>
                        <div className="shrink-0 text-right">
                          {t.due_date && (
                            <p className={`text-xs font-semibold ${done ? "text-slate-400" : "text-[#1D4ED8]"}`}>{new Date(t.due_date).toLocaleDateString("it-IT", { day: "2-digit", month: "short" })}</p>
                          )}
                          {t.priority === "high" && !done && (
                            <p className="text-[10px] uppercase tracking-wider text-rose-500 font-bold mt-0.5">Urgente</p>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Orders */}
      <Section title={ev.status === "approved" ? "Ordini ufficiali ai fornitori" : "Anteprima ordini ai fornitori"} icon={Truck}>
        {(!ev.orders || ev.orders.length === 0) ? (
          <p className="text-sm text-slate-500">Nessun fornitore attivo ha listino per gli ingredienti richiesti. <button onClick={() => navigate("/fornitori")} className="text-[#2563EB] underline">Aggiungi fornitori</button> oppure <button onClick={() => navigate("/fatture")} className="text-[#2563EB] underline">carica una fattura</button>.</p>
        ) : (
          <div className="space-y-4">
            {ev.status !== "approved" && (
              <div className="text-xs px-3 py-2 rounded-lg bg-amber-50 text-amber-700 border border-amber-200">
                Questa è una <strong>anteprima</strong>. Gli ordini ufficiali (con numero progressivo) saranno generati solo dopo l'<strong>approvazione del preventivo</strong>.
              </div>
            )}
            {ev.orders.map((o, idx) => {
              const isFree = o.is_free || o.supplier_id == null;
              return (
              <div key={o.order_number || o.supplier_id || `free-${idx}`} className={`border rounded-xl p-4 ${isFree ? "border-amber-300 bg-amber-50/40" : "border-slate-200"}`}>
                <div className="flex flex-wrap gap-2 justify-between items-start mb-3">
                  <div>
                    {o.order_number && (
                      <p className="text-xs uppercase tracking-wider text-[#2563EB] font-semibold">#{o.order_number}</p>
                    )}
                    <h4 className="font-ui font-semibold text-[#0F2741] flex items-center gap-2">
                      {isFree && <span className="text-xs px-2 py-0.5 rounded-full bg-amber-200 text-amber-800 uppercase tracking-wider">Lista spesa</span>}
                      {o.supplier_name}
                    </h4>
                    {isFree && (
                      <p className="text-xs text-amber-700 mt-1">Ingredienti senza fornitore associato · prezzi stimati dal DB ingredienti</p>
                    )}
                    {o.created_at && (
                      <p className="text-xs text-slate-500">Emesso il {new Date(o.created_at).toLocaleDateString("it-IT")}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {o.status && (
                      <span className={`text-xs px-2 py-1 rounded-full font-semibold ${ORDER_STATUS_CLASS[o.status] || "bg-slate-100 text-slate-600"}`} data-testid={`order-status-${o.order_number}`}>
                        {ORDER_STATUS_LABEL[o.status] || o.status}
                      </span>
                    )}
                    <span className="font-display text-lg text-[#2563EB]">{fmtEur(o.total)}</span>
                  </div>
                </div>
                <table className="w-full text-sm">
                  <thead className="text-xs uppercase text-slate-500"><tr><th className="text-left py-1">Ingrediente</th><th className="text-right">Qtà (kg)</th><th className="text-right">{isFree ? "€/kg stim." : "Prezzo/kg"}</th><th className="text-right">Totale</th></tr></thead>
                  <tbody>
                    {o.items.map((it, i) => (
                      <tr key={i} className="border-t border-slate-100">
                        <td className="py-2 capitalize">{it.ingredient}</td>
                        <td className="py-2 text-right">{it.quantity_kg.toFixed(2)}</td>
                        <td className="py-2 text-right">€{it.price_per_kg.toFixed(2)}</td>
                        <td className="py-2 text-right font-semibold">{fmtEur(it.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {isFree && (
                  <div className="mt-3 pt-3 border-t border-amber-200 flex flex-wrap gap-2 justify-end">
                    <button onClick={() => navigate("/fornitori")} className="btn-ghost border border-slate-200">
                      <Truck className="w-3.5 h-3.5" /> Crea fornitore per questi ingredienti
                    </button>
                  </div>
                )}
                {o.order_number && !isFree && (
                  <div className="mt-4 pt-3 border-t border-slate-100 flex flex-wrap gap-2 justify-end">
                    <button onClick={() => printOrder(o)} data-testid={`btn-print-${o.order_number}`} className="btn-ghost border border-slate-200">
                      <Printer className="w-3.5 h-3.5" /> Stampa
                    </button>
                    {o.status !== "sent" && (
                      <button onClick={() => updateOrderStatus(o.order_number, "sent")} className="btn-ghost border border-slate-200">
                        <Send className="w-3.5 h-3.5" /> Segna come inviato
                      </button>
                    )}
                    {o.status === "sent" && (
                      <button onClick={() => updateOrderStatus(o.order_number, "delivered")} className="btn-ghost border border-slate-200">
                        <CheckCircle2 className="w-3.5 h-3.5" /> Segna consegnato
                      </button>
                    )}
                  </div>
                )}
                {o.order_number && isFree && (
                  <div className="mt-3 pt-3 border-t border-amber-200 flex flex-wrap gap-2 justify-end">
                    <button onClick={() => printOrder(o)} className="btn-ghost border border-slate-200">
                      <Printer className="w-3.5 h-3.5" /> Stampa lista spesa
                    </button>
                  </div>
                )}
              </div>
              );
            })}
          </div>
        )}
      </Section>

      {/* Menu */}
      <Section title="Menù" icon={ChefHat} action={
        <button onClick={genMenu} disabled={loadingMenu} className="btn-ghost border border-slate-200">
          {loadingMenu ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />} Genera con AI
        </button>}>
        {ev.menu_text ? (
          <div className="font-display text-base text-[#0F2741] whitespace-pre-line leading-relaxed bg-[#FFFBEB] border border-[#F59E0B]/20 rounded-xl p-6">{ev.menu_text}</div>
        ) : (
          <div className="space-y-3">
            {Object.entries(ev.dishes || {}).map(([cat, ids]) => {
              if (!ids?.length) return null;
              const list = chosenDishes.filter((d) => ids.includes(d.id));
              return (
                <div key={cat}>
                  <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">{cat}</p>
                  <p className="text-sm text-[#0F2741]">{list.map((d) => d.name).join(" · ")}</p>
                </div>
              );
            })}
          </div>
        )}
      </Section>

      {/* Artists & Services */}
      {(chosenArtists.length > 0 || photographers.length > 0 || drones.length > 0) && (
        <Section title="Servizi & Artisti" icon={Music2}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {photographers.map((p) => {
              const pb = (ev.photographers_bookings || []).find((b) => b.artist_id === p.id) || {};
              const price = pb.custom_price ?? p.price_eur;
              return (
                <div key={p.id} className="border border-slate-200 rounded-xl p-4">
                  <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-[#2563EB] font-semibold">
                    <Camera className="w-3 h-3" /> Fotografo
                  </div>
                  <p className="font-ui font-semibold text-[#0F2741] mt-1">{p.name}</p>
                  <p className="text-xs text-slate-500">{p.phone} · {fmtEur(price)}</p>
                  {(pb.arrival_time || pb.hours) && (
                    <p className="text-xs text-slate-400 mt-1">Arrivo {pb.arrival_time || "—"}{pb.hours ? ` · ${pb.hours}h` : ""}</p>
                  )}
                </div>
              );
            })}
            {drones.map((p) => {
              const pb = (ev.drones_bookings || []).find((b) => b.artist_id === p.id) || {};
              const price = pb.custom_price ?? p.price_eur;
              return (
                <div key={p.id} className="border border-slate-200 rounded-xl p-4">
                  <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-[#2563EB] font-semibold">
                    <Plane className="w-3 h-3" /> Drone
                  </div>
                  <p className="font-ui font-semibold text-[#0F2741] mt-1">{p.name}</p>
                  <p className="text-xs text-slate-500">{p.phone} · {fmtEur(price)}</p>
                  {(pb.arrival_time || pb.hours) && (
                    <p className="text-xs text-slate-400 mt-1">Arrivo {pb.arrival_time || "—"}{pb.hours ? ` · ${pb.hours}h` : ""}</p>
                  )}
                </div>
              );
            })}
            {(ev.artists_bookings || []).map((b, i) => {
              const a = artists.find((x) => x.id === b.artist_id);
              if (!a) return null;
              const slot = b.arrival_time ? `${b.arrival_time}${b.hours ? ` · ${b.hours}h` : ""}` : `${b.start_time || ""}${b.end_time ? `-${b.end_time}` : ""}`;
              return (
                <div key={i} className="border border-slate-200 rounded-xl p-4">
                  <div className="text-xs uppercase tracking-wider text-[#2563EB] font-semibold">{a.category}</div>
                  <p className="font-ui font-semibold text-[#0F2741] mt-1">{a.name}</p>
                  <p className="text-xs text-slate-500">{slot} · {fmtEur(b.custom_price ?? a.price_eur)}</p>
                </div>
              );
            })}
            {/* legacy timeline artists */}
            {chosenArtists.filter((a) => !(ev.artists_bookings || []).some((b) => b.artist_id === a.id) && !photographerIds.includes(a.id) && !droneIds.includes(a.id)).map((a) => (
              <div key={a.id} className="border border-slate-200 rounded-xl p-4">
                <div className="text-xs uppercase tracking-wider text-[#1D4ED8] font-semibold">{a.category}</div>
                <p className="font-ui font-semibold text-[#0F2741] mt-1">{a.name}</p>
                <p className="text-xs text-slate-500">{a.phone} · {fmtEur(a.price_eur)}</p>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Cake */}
      {ev.cake?.enabled && (
        <Section title="Torta" icon={Cake}>
          <div className="flex items-center justify-between p-4 rounded-xl bg-gradient-to-br from-[#EFF6FF] to-white border border-[#2563EB]/20">
            <div>
              <p className="text-xs uppercase tracking-wider text-[#2563EB] font-semibold">{ev.cake.tier}</p>
              <p className="font-display text-2xl text-[#0F2741] mt-1">Torta {ev.cake.tier?.toUpperCase()}</p>
              <p className="text-sm text-slate-500 mt-1">Per {ev.cake.persons || ev.guests} persone</p>
            </div>
            <p className="font-display text-3xl text-[#2563EB]">{fmtEur(ev.cake.price)}</p>
          </div>
        </Section>
      )}

      {/* Quote */}
      {ev.quote_text && (
        <Section title="Preventivo Cliente" icon={FileText} action={
          <div className="flex gap-2 flex-wrap">
            <button onClick={downloadQuotePDF} data-testid="btn-download-pdf" className="btn-primary">
              <Download className="w-4 h-4" /> Scarica PDF
            </button>
            <button onClick={printQuote} data-testid="btn-print-quote" className="btn-ghost border border-slate-200">
              <Printer className="w-4 h-4" /> Stampa
            </button>
          </div>
        }>
          <div className="bg-white border-2 border-[#0F2741]/10 rounded-xl p-8 shadow-sm">
            <div className="text-center mb-6">
              <p className="text-xs uppercase tracking-[0.2em] text-[#2563EB] font-semibold">Preventivo</p>
              <h2 className="font-display text-3xl text-[#0F2741] mt-1">{ev.client_name || "Gentile Cliente"}</h2>
              <p className="text-sm text-slate-500 mt-1">{ev.event_date} · {ev.guests} ospiti</p>
            </div>
            <div className="font-body text-sm text-slate-700 whitespace-pre-line leading-7">{ev.quote_text}</div>
            <div className="mt-6 pt-6 border-t border-slate-200 flex justify-between items-center">
              <p className="text-xs text-slate-400">Generato con AI · Event System 360</p>
              <p className="font-display text-3xl text-[#2563EB]">{fmtEur(ev.final_price)}</p>
            </div>
          </div>
          <p className="mt-3 text-xs text-slate-500 italic">Scarica il PDF e inoltralo al cliente direttamente dalla tua casella email per maggiore personalizzazione.</p>
        </Section>
      )}

      {rejectModal && (
        <RejectQuoteModal
          onClose={() => setRejectModal(false)}
          onConfirm={rejectQuote}
          loading={rejecting}
        />
      )}
    </div>
  );
}

function RejectQuoteModal({ onClose, onConfirm, loading }) {
  const [reason, setReason] = useState("");
  const submit = (e) => {
    e.preventDefault();
    onConfirm(reason.trim());
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 grid place-items-center p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-white rounded-xl p-6 w-full max-w-md space-y-4" data-testid="reject-quote-modal">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <XCircle className="w-5 h-5 text-rose-500" />
            <h3 className="font-display text-2xl text-[#0F2741]">Preventivo Rifiutato</h3>
          </div>
          <button type="button" onClick={onClose}><X className="w-5 h-5 text-slate-400" /></button>
        </div>
        <p className="text-sm text-slate-500">Conferma che il cliente ha rifiutato il preventivo. Puoi aggiungere una motivazione per archivio (es. prezzo troppo alto, data non disponibile, ha scelto un altro fornitore).</p>
        <div>
          <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Motivazione (opzionale)</label>
          <textarea
            data-testid="input-reject-reason"
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Es. Cliente ha trovato un'alternativa più economica..."
            className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:ring-2 focus:ring-rose-400 outline-none"
          />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="btn-ghost">Annulla</button>
          <button type="submit" disabled={loading} data-testid="btn-confirm-reject" className="btn-primary bg-rose-600 hover:bg-rose-700">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4" />} Conferma rifiuto
          </button>
        </div>
      </form>
    </div>
  );
}

function Section({ title, icon: Icon, children, action }) {
  return (
    <section className="bg-white rounded-xl border border-slate-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-ui font-semibold text-lg text-[#0F2741] flex items-center gap-2"><Icon className="w-4 h-4 text-[#2563EB]" /> {title}</h2>
        {action}
      </div>
      {children}
    </section>
  );
}
function CostCard({ label, value, sale, red }) {
  const hasMarkup = sale && sale !== value;
  return (
    <div className={`p-4 rounded-xl border ${red ? "bg-rose-50 border-rose-300" : "bg-white border-slate-200"}`}>
      <p className={`text-[10px] uppercase tracking-wider font-semibold leading-tight ${red ? "text-rose-700" : "text-slate-500"}`}>
        {label}{red && <span className="ml-1">⚠</span>}
      </p>
      <p className={`text-xs mt-2 font-semibold ${red ? "text-rose-600" : "text-amber-700"}`}>Costo: {value}</p>
      <p className={`font-display text-lg mt-0.5 ${red ? "text-rose-700" : hasMarkup ? "text-emerald-700" : "text-[#0F2741]"}`}>{sale || value}</p>
    </div>
  );
}

function StaffEditor({ ev, eventId, onUpdate }) {
  const sp = ev.staff_plan || {};
  const overrides = ev.staff_overrides || {};
  const [s, setS] = useState({
    waiters: overrides.waiters ?? sp.waiters ?? 0,
    chefs: overrides.chefs ?? sp.chefs ?? 0,
    kitchen_helpers: overrides.kitchen_helpers ?? sp.kitchen_helpers ?? 0,
    bartender: overrides.bartender ?? sp.bartender ?? 0,
  });
  const [saving, setSaving] = useState(false);

  // Sync when ev changes from outside (after recalc)
  useEffect(() => {
    const ov = ev.staff_overrides || {};
    const cur = ev.staff_plan || {};
    setS({
      waiters: ov.waiters ?? cur.waiters ?? 0,
      chefs: ov.chefs ?? cur.chefs ?? 0,
      kitchen_helpers: ov.kitchen_helpers ?? cur.kitchen_helpers ?? 0,
      bartender: ov.bartender ?? cur.bartender ?? 0,
    });
  }, [ev.staff_plan, ev.staff_overrides]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/events/${eventId}/staff-overrides`, s);
      toast.success("Staff aggiornato. Ricalcolo costi...");
      if (onUpdate) await onUpdate();
    } catch {
      toast.error("Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    setSaving(true);
    try {
      await api.put(`/events/${eventId}/staff-overrides`, {});
      toast.success("Auto-calcolo ripristinato");
      if (onUpdate) await onUpdate();
    } catch {
      toast.error("Errore reset");
    } finally {
      setSaving(false);
    }
  };

  const rows = [
    { key: "waiters", label: "Camerieri", hourly: 18 },
    { key: "chefs", label: "Chef", hourly: 30 },
    { key: "kitchen_helpers", label: "Aiuto cucina", hourly: 15 },
    { key: "bartender", label: "Bartender", hourly: 20 },
  ];
  const duration = (sp.duration_hours || 5) + 2;
  const totalCost = rows.reduce((sum, r) => sum + (parseFloat(s[r.key]) || 0) * r.hourly * duration, 0);

  return (
    <div data-testid="staff-editor">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {rows.map((r) => {
          const ov = (ev.staff_overrides || {})[r.key];
          const isOverridden = ov !== undefined && ov !== null && ov !== "";
          return (
            <div key={r.key} className="bg-slate-50 rounded-lg p-3 border border-slate-200">
              <div className="flex justify-between items-center mb-2">
                <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{r.label}</label>
                {isOverridden && <span className="text-[9px] uppercase font-bold tracking-wider text-emerald-600">manuale</span>}
              </div>
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => setS({ ...s, [r.key]: Math.max(0, (parseFloat(s[r.key]) || 0) - 1) })} className="w-7 h-7 rounded-full border border-slate-300 text-slate-500 hover:bg-slate-200" data-testid={`staff-dec-${r.key}`}>−</button>
                <input
                  type="number" min="0" step="1"
                  value={s[r.key]}
                  onChange={(e) => setS({ ...s, [r.key]: e.target.value === "" ? 0 : parseFloat(e.target.value) })}
                  data-testid={`staff-input-${r.key}`}
                  className="flex-1 px-2 py-1.5 rounded border border-slate-200 text-center font-display text-xl text-[#0F2741]"
                />
                <button type="button" onClick={() => setS({ ...s, [r.key]: (parseFloat(s[r.key]) || 0) + 1 })} className="w-7 h-7 rounded-full border border-slate-300 text-slate-500 hover:bg-slate-200" data-testid={`staff-inc-${r.key}`}>+</button>
              </div>
              <p className="text-[10px] text-slate-400 mt-1">€{r.hourly}/h × {duration}h</p>
            </div>
          );
        })}
        <div className="bg-[#EFF6FF] rounded-lg p-3 border border-[#2563EB]/30">
          <p className="text-xs uppercase tracking-wider text-[#1D4ED8] font-semibold">Durata</p>
          <p className="font-display text-2xl text-[#0F2741] mt-1">{sp.duration_hours}h</p>
          <p className="text-[10px] text-slate-400 mt-1">+2h prep</p>
        </div>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3 mt-4 pt-3 border-t border-slate-100">
        <p className="text-sm text-slate-500">Costo staff stimato live: <strong className="text-[#0F2741]">€{totalCost.toFixed(2)}</strong>{ev.staff_plan?.staff_cost_eur ? <span className="text-xs text-slate-400 ml-2">(salvato: {fmtEur(ev.staff_plan.staff_cost_eur)})</span> : null}</p>
        <div className="flex gap-2">
          <button onClick={reset} disabled={saving} className="btn-ghost border border-slate-200 text-slate-600 text-xs">Auto-calcolo</button>
          <button onClick={save} disabled={saving} data-testid="btn-save-staff" className="btn-primary">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Salva e ricalcola"}
          </button>
        </div>
      </div>
    </div>
  );
}

function MarkupByCategoryEditor({ ev, eventId, onUpdate }) {
  const initial = ev.markup_by_category || {};
  const [m, setM] = useState({
    food: initial.food ?? 0,
    drinks: initial.drinks ?? 0,
    staff: initial.staff ?? 0,
    artists: initial.artists ?? 0,
    photographers: initial.photographers ?? 0,
    cake: initial.cake ?? 0,
    location: initial.location ?? 0,
    decorations: initial.decorations ?? 0,
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/events/${eventId}/markup-by-category`, m);
      toast.success("Ricarichi salvati. Ricalcolo in corso...");
      if (onUpdate) await onUpdate();
    } catch {
      toast.error("Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const rows = [
    { key: "food", label: "Cibo", hint: "Ricarico sul food cost reale (lascia 0 per usare costo esatto)" },
    { key: "drinks", label: "Bevande" },
    { key: "staff", label: "Staff" },
    { key: "artists", label: "Artisti" },
    { key: "photographers", label: "Fotografi & Drone" },
    { key: "cake", label: "Torta" },
    { key: "location", label: "Location" },
    { key: "decorations", label: "Addobbi" },
  ];

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="markup-by-category">
      <div className="flex justify-between items-start mb-4">
        <div>
          <h3 className="font-ui font-semibold text-[#0F2741] flex items-center gap-2"><Percent className="w-4 h-4 text-emerald-600" /> Ricarico differenziato per voce</h3>
          <p className="text-xs text-slate-500 mt-1">Imposta il ricarico % per ogni categoria. Se valorizzato, sostituisce il ricarico globale.</p>
        </div>
        <button onClick={save} disabled={saving} data-testid="btn-save-markup-categories" className="btn-primary">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Salva e ricalcola"}
        </button>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {rows.map((r) => (
          <div key={r.key} className="flex flex-col gap-1">
            <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{r.label}</label>
            <div className="flex items-center gap-1">
              <input
                type="number" min="0" step="1"
                value={m[r.key]}
                onChange={(e) => setM({ ...m, [r.key]: e.target.value === "" ? 0 : parseFloat(e.target.value) })}
                data-testid={`markup-input-${r.key}`}
                className="flex-1 px-2 py-1.5 rounded border border-slate-200 text-sm text-right font-semibold"
              />
              <span className="text-xs text-slate-400">%</span>
            </div>
          </div>
        ))}
      </div>
      <p className="text-[11px] text-slate-400 mt-3">⚙️ Il "Cibo" è il food cost reale (somma di tutti gli ingredienti per ospite). Lascia 0% per riportarlo esattamente al costo.</p>
    </div>
  );
}
function Pill({ label, value }) {
  return (
    <div className="text-center p-3 rounded-lg bg-[#EFF6FF] border border-[#2563EB]/20">
      <p className="text-xs uppercase tracking-wider text-[#1D4ED8] font-semibold">{label}</p>
      <p className="font-display text-2xl text-[#0F2741] mt-1">{value}</p>
    </div>
  );
}

function PaymentTermsSection({ ev, eventId, onUpdate }) {
  const [presets, setPresets] = useState([]);
  const [defaultClauses, setDefaultClauses] = useState([]);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const pt = ev.payment_terms || {};
  const schedule = pt.schedule || [];
  const finalPrice = Number(ev.final_price || 0);

  useEffect(() => {
    api.get("/payment-presets").then((r) => {
      setPresets(r.data.presets || []);
      setDefaultClauses(r.data.default_legal_clauses || []);
    }).catch(() => {});
  }, []);

  const applyPreset = async (key) => {
    setSaving(true);
    try {
      await api.put(`/events/${eventId}/payment-terms`, { preset: key });
      await onUpdate();
      toast.success("Modalità di pagamento aggiornata");
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setSaving(false); }
  };

  const saveCustom = async (custom) => {
    setSaving(true);
    try {
      await api.put(`/events/${eventId}/payment-terms`, custom);
      await onUpdate();
      setEditing(false);
      toast.success("Condizioni salvate");
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setSaving(false); }
  };

  const totalPct = schedule.reduce((s, x) => s + (Number(x.pct) || 0), 0);

  return (
    <Section title="Modalità di pagamento" icon={Wallet} action={
      <button onClick={() => setEditing(true)} data-testid="btn-edit-payment-terms" className="btn-ghost border border-slate-200">
        <Edit3 className="w-3.5 h-3.5" /> {schedule.length ? "Modifica" : "Configura"}
      </button>
    }>
      {schedule.length === 0 ? (
        <div data-testid="payment-empty">
          <p className="text-sm text-slate-500 mb-4">Nessuna modalità di pagamento configurata. Scegli un preset o crea una rateizzazione personalizzata.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {presets.map((p) => (
              <button
                key={p.key}
                onClick={() => applyPreset(p.key)}
                disabled={saving}
                data-testid={`preset-${p.key}`}
                className="text-left p-4 rounded-xl border-2 border-slate-200 hover:border-[#2563EB] hover:bg-[#EFF6FF] transition-colors"
              >
                <p className="font-ui font-semibold text-[#0F2741]">{p.label}</p>
                <p className="text-xs text-slate-500 mt-1">{p.description}</p>
              </button>
            ))}
          </div>
        </div>
      ) : (
        <div data-testid="payment-terms-view">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
              {pt.preset === "custom" ? "Rateizzazione personalizzata" : presets.find((p) => p.key === pt.preset)?.label || pt.preset}
            </p>
            <span className={`text-xs font-semibold ${totalPct === 100 ? "text-emerald-600" : "text-rose-600"}`}>
              Totale: {totalPct.toFixed(0)}%
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase text-slate-500 tracking-wider">
                <tr><th className="text-left px-3 py-2">Rata</th><th className="text-left px-3 py-2">Scadenza</th><th className="text-right px-3 py-2">%</th><th className="text-right px-3 py-2">Importo</th></tr>
              </thead>
              <tbody>
                {schedule.map((s, i) => {
                  const amount = finalPrice * (Number(s.pct) || 0) / 100;
                  let when = "—";
                  if (s.due_on_acceptance) when = "All'accettazione";
                  else if (s.days_before === 0) when = "Giorno dell'evento";
                  else if (s.days_before > 0) when = `${s.days_before} giorni prima`;
                  return (
                    <tr key={i} className="border-t border-slate-100">
                      <td className="px-3 py-2 font-medium text-[#0F2741]">{s.label}</td>
                      <td className="px-3 py-2 text-slate-500">{when}</td>
                      <td className="px-3 py-2 text-right font-semibold text-[#2563EB]">{s.pct}%</td>
                      <td className="px-3 py-2 text-right font-display text-base text-[#0F2741]">€ {amount.toLocaleString("it-IT", { minimumFractionDigits: 2 })}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {pt.notes && <p className="text-xs text-slate-500 mt-3 italic">Note: {pt.notes}</p>}
          <p className="text-xs text-slate-400 mt-3">
            {pt.vat_included === false ? "Prezzi IVA esclusa" : "Prezzi IVA inclusa"} · {(pt.legal_clauses || []).length} clausole legali nel preventivo
          </p>
        </div>
      )}

      {editing && (
        <PaymentTermsEditor
          initial={pt}
          presets={presets}
          defaultClauses={defaultClauses}
          onClose={() => setEditing(false)}
          onSave={saveCustom}
          saving={saving}
        />
      )}
    </Section>
  );
}

function PaymentTermsEditor({ initial, presets, defaultClauses, onClose, onSave, saving }) {
  const [tab, setTab] = useState(initial.preset && initial.preset !== "custom" ? "preset" : "custom");
  const [selectedPreset, setSelectedPreset] = useState(initial.preset && initial.preset !== "custom" ? initial.preset : "");
  const [schedule, setSchedule] = useState(initial.schedule?.length ? initial.schedule : [
    { label: "Acconto all'accettazione", pct: 30, days_before: null, due_on_acceptance: true },
    { label: "Saldo il giorno dell'evento", pct: 70, days_before: 0, due_on_acceptance: false },
  ]);
  const [vatIncluded, setVatIncluded] = useState(initial.vat_included !== false);
  const [notes, setNotes] = useState(initial.notes || "");
  const [clauses, setClauses] = useState((initial.legal_clauses && initial.legal_clauses.length ? initial.legal_clauses : defaultClauses));

  useEffect(() => {
    if (clauses.length === 0 && defaultClauses.length) setClauses(defaultClauses);
  }, [defaultClauses]); // eslint-disable-line

  const totalPct = schedule.reduce((s, x) => s + (Number(x.pct) || 0), 0);
  const validCustom = Math.abs(totalPct - 100) < 0.5 && schedule.every((s) => s.label && (Number(s.pct) || 0) > 0);

  const updateRow = (i, patch) => {
    const next = schedule.map((s, idx) => idx === i ? { ...s, ...patch } : s);
    setSchedule(next);
  };
  const addRow = () => setSchedule([...schedule, { label: "Nuova rata", pct: 0, days_before: 7, due_on_acceptance: false }]);
  const removeRow = (i) => setSchedule(schedule.filter((_, idx) => idx !== i));

  const updateClause = (i, value) => setClauses(clauses.map((c, idx) => idx === i ? value : c));
  const removeClause = (i) => setClauses(clauses.filter((_, idx) => idx !== i));
  const addClause = () => setClauses([...clauses, ""]);
  const restoreClauses = () => setClauses(defaultClauses);

  const submit = (e) => {
    e.preventDefault();
    const cleanClauses = clauses.map((c) => c.trim()).filter(Boolean);
    if (tab === "preset" && selectedPreset) {
      onSave({ preset: selectedPreset, vat_included: vatIncluded, notes, legal_clauses: cleanClauses });
    } else {
      if (!validCustom) {
        toast.error("Verifica che il totale sia 100% e che tutte le rate abbiano nome+percentuale");
        return;
      }
      onSave({ schedule, vat_included: vatIncluded, notes, legal_clauses: cleanClauses });
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 grid place-items-center p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-white rounded-xl p-6 w-full max-w-3xl space-y-5 max-h-[92vh] overflow-y-auto" data-testid="payment-terms-editor">
        <div className="flex justify-between items-center sticky top-0 bg-white pb-3 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <Wallet className="w-5 h-5 text-[#2563EB]" />
            <h3 className="font-display text-2xl text-[#0F2741]">Modalità di pagamento e clausole</h3>
          </div>
          <button type="button" onClick={onClose}><X className="w-5 h-5 text-slate-400" /></button>
        </div>

        <div className="flex gap-2">
          <button type="button" onClick={() => setTab("preset")} className={`px-4 py-2 rounded-full text-sm font-medium ${tab === "preset" ? "bg-[#0F2741] text-white" : "bg-slate-100 text-slate-600"}`}>Preset</button>
          <button type="button" onClick={() => setTab("custom")} className={`px-4 py-2 rounded-full text-sm font-medium ${tab === "custom" ? "bg-[#0F2741] text-white" : "bg-slate-100 text-slate-600"}`}>Personalizzato</button>
        </div>

        {tab === "preset" && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {presets.map((p) => (
              <button
                key={p.key}
                type="button"
                onClick={() => setSelectedPreset(p.key)}
                data-testid={`editor-preset-${p.key}`}
                className={`text-left p-4 rounded-xl border-2 transition-colors ${selectedPreset === p.key ? "border-[#2563EB] bg-[#EFF6FF]" : "border-slate-200 hover:border-[#2563EB]"}`}
              >
                <p className="font-ui font-semibold text-[#0F2741]">{p.label}</p>
                <p className="text-xs text-slate-500 mt-1">{p.description}</p>
                <div className="mt-2 flex gap-1.5 flex-wrap">
                  {p.schedule.map((s, i) => (<span key={i} className="text-[10px] px-2 py-0.5 rounded-full bg-white border border-slate-200 text-slate-600">{s.pct}% · {s.due_on_acceptance ? "acc." : s.days_before === 0 ? "evento" : `${s.days_before}gg`}</span>))}
                </div>
              </button>
            ))}
          </div>
        )}

        {tab === "custom" && (
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <p className="text-sm text-slate-600">Definisci rate personalizzate. La somma delle percentuali deve essere <strong>100%</strong>.</p>
              <span className={`text-sm font-semibold ${Math.abs(totalPct - 100) < 0.5 ? "text-emerald-600" : "text-rose-600"}`}>Totale: {totalPct.toFixed(0)}%</span>
            </div>
            <div className="space-y-2">
              {schedule.map((s, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center p-3 rounded-lg border border-slate-200">
                  <input value={s.label} onChange={(e) => updateRow(i, { label: e.target.value })} placeholder="Es. Acconto" className="col-span-4 px-2 py-1.5 rounded border border-slate-200 text-sm" data-testid={`row-${i}-label`} />
                  <div className="col-span-3 flex items-center gap-1">
                    <input type="number" min="1" max="100" value={s.pct} onChange={(e) => updateRow(i, { pct: parseFloat(e.target.value) || 0 })} className="w-full px-2 py-1.5 rounded border border-slate-200 text-sm text-right" data-testid={`row-${i}-pct`} />
                    <span className="text-slate-500 text-sm">%</span>
                  </div>
                  <div className="col-span-4 flex items-center gap-2">
                    <select
                      value={s.due_on_acceptance ? "acceptance" : (s.days_before === 0 ? "event_day" : "before")}
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v === "acceptance") updateRow(i, { due_on_acceptance: true, days_before: null });
                        else if (v === "event_day") updateRow(i, { due_on_acceptance: false, days_before: 0 });
                        else updateRow(i, { due_on_acceptance: false, days_before: s.days_before && s.days_before > 0 ? s.days_before : 7 });
                      }}
                      className="px-2 py-1.5 rounded border border-slate-200 text-sm"
                    >
                      <option value="acceptance">All'accettazione</option>
                      <option value="before">Giorni prima evento</option>
                      <option value="event_day">Giorno evento</option>
                    </select>
                    {!s.due_on_acceptance && s.days_before > 0 && (
                      <input type="number" min="1" value={s.days_before} onChange={(e) => updateRow(i, { days_before: parseInt(e.target.value) || 1 })} className="w-16 px-2 py-1.5 rounded border border-slate-200 text-sm text-right" />
                    )}
                  </div>
                  <button type="button" onClick={() => removeRow(i)} className="col-span-1 text-rose-400 hover:text-rose-600 justify-self-end"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
            <button type="button" onClick={addRow} className="btn-ghost border border-dashed border-slate-300 w-full"><Plus className="w-4 h-4" /> Aggiungi rata</button>
          </div>
        )}

        <div className="border-t border-slate-100 pt-4 space-y-3">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={vatIncluded} onChange={(e) => setVatIncluded(e.target.checked)} className="accent-[#2563EB]" />
            Prezzi indicati IVA inclusa
          </label>
          <div>
            <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Note pagamento (opzionale)</label>
            <input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Es. Bonifico bancario IBAN..." className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-200 text-sm" />
          </div>
        </div>

        <div className="border-t border-slate-100 pt-4">
          <div className="flex justify-between items-center mb-2">
            <p className="text-sm font-ui font-semibold text-[#0F2741]">Clausole legali nel preventivo</p>
            <button type="button" onClick={restoreClauses} className="text-xs text-[#2563EB] underline">Ripristina default</button>
          </div>
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {clauses.map((c, i) => (
              <div key={i} className="flex gap-2 items-start">
                <span className="text-xs text-slate-400 mt-2 w-5">{i + 1}.</span>
                <textarea rows={2} value={c} onChange={(e) => updateClause(i, e.target.value)} className="flex-1 px-2 py-1.5 rounded border border-slate-200 text-xs" />
                <button type="button" onClick={() => removeClause(i)} className="text-rose-400 hover:text-rose-600 mt-2"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            ))}
          </div>
          <button type="button" onClick={addClause} className="btn-ghost border border-dashed border-slate-300 w-full mt-2 text-xs"><Plus className="w-3.5 h-3.5" /> Aggiungi clausola</button>
        </div>

        <div className="flex justify-end gap-2 pt-3 border-t border-slate-100 sticky bottom-0 bg-white">
          <button type="button" onClick={onClose} className="btn-ghost">Annulla</button>
          <button type="submit" disabled={saving || (tab === "preset" && !selectedPreset) || (tab === "custom" && !validCustom)} data-testid="btn-save-payment-terms" className="btn-primary">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Salva condizioni
          </button>
        </div>
      </form>
    </div>
  );
}
